import { supabaseClient } from './supabase.js';
import { signOutLocally } from './impersonation';
import { 
  getConstraints, 
  getFields, 
  invalidateAndRefreshAllQueriesForTable,
  invalidateAllQueriesForTableNoRefetch,
  invalidateAndRefetch,
  invalidateAllMetadataCache
} from './metadata/index.js';
import { smartCast } from './type-guards';
import { databaseValuesAreEqual, normalizeStaleWriteFilterValue } from './databaseValueComparison';
import {
  captureHandledOperationError,
  captureStaleWriteConflict,
  type StaleWriteDetectionStage,
} from './staleWriteTelemetry';
import { getTableCategory, isOperationAllowed } from './table-categories';
import { captureProductEvent } from './productAnalytics';
import type { 
  ConstraintInfo
} from '../types/metadata.js';
import type { AnyRecord } from '../types/api.js';

function trackRecordOperation(
  operation: 'created' | 'updated' | 'upserted' | 'deleted' | 'failed',
  tableName: string,
  schema: string,
  data?: AnyRecord[] | null,
  whereClause?: Record<string, any>,
  attemptedOperation?: 'create' | 'update' | 'upsert' | 'delete',
): void {
  const ids = (data ?? []).map((record) => record.id).filter(Boolean);
  const recordIds = ids.length > 0 ? ids : whereClause?.id ? [whereClause.id] : [];
  captureProductEvent(`record_${operation}`, {
    table: tableName,
    schema,
    recordIds: recordIds.join(','),
    recordCount: recordIds.length,
    operation: attemptedOperation ?? operation,
  });
}

// Interface for SupabaseOperationError constructor parameters
interface SupabaseOperationErrorParams {
  originalError?: any;
  status?: number;
  constraintInfo?: ConstraintInfo | null;
  relevantField?: string | null;
  dbCol?: string | null;
  operation?: string | null;
  tableName?: string | null;
  staleFields?: string[] | null;
}

// Interface for debug information
interface DebugInfo {
  operation?: string | null;
  tableName?: string | null;
  relevantField?: string | null;
  status?: number;
  isConstraintError: boolean;
  constraintName?: string;
  originalMessage?: string;
  dbCol?: string | null;
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Enhanced error class for structured error handling
 */
export class SupabaseOperationError extends Error {
  public readonly originalError?: any;
  public readonly status?: number;
  public readonly constraintInfo?: ConstraintInfo | null;
  public readonly relevantField?: string | null;
  public readonly dbCol?: string | null;
  public readonly operation?: string | null;
  public readonly tableName?: string | null;
  public readonly isConstraintError: boolean;
  /** Columns another user changed under us; set only on a stale-write rejection. */
  public readonly staleFields?: string[] | null;
  public readonly isStaleWrite: boolean;

  constructor({
    originalError,
    status,
    constraintInfo = null,
    relevantField = null,
    dbCol = null,
    operation = null,
    tableName = null,
    staleFields = null
  }: SupabaseOperationErrorParams) {
    // Use constraint error message if available, otherwise use original error message
    const message = constraintInfo?.error_message || originalError?.message || 'Operation failed';
    super(message);
    
    this.name = 'SupabaseOperationError';
    this.originalError = originalError;
    this.status = status;
    this.constraintInfo = constraintInfo;
    this.relevantField = relevantField;
    this.dbCol = dbCol;
    this.operation = operation;
    this.tableName = tableName;
    this.isConstraintError = !!constraintInfo;
    this.staleFields = staleFields;
    this.isStaleWrite = Array.isArray(staleFields) && staleFields.length > 0;
  }

  /**
   * Returns a user-friendly error message
   */
  getUserMessage(): string {
    if (this.constraintInfo) {
      return this.constraintInfo.error_message;
    }

    // Names the fields someone else changed — more use than the generic 409 text below.
    if (this.isStaleWrite) {
      return this.message;
    }

    // Handle JWT expiration
    if (this.originalError?.code === 'PGRST301' || this.originalError?.message?.includes('JWT expired')) {
      return 'Your session has expired. Please log in again.';
    }

    // Handle PostgreSQL raised exceptions (P0001) - these contain user-friendly messages
    if (this.originalError?.code === 'P0001' && this.originalError?.message) {
      return this.originalError.message;
    }

    // PT409 is how database guards refuse a write without PostgREST retrying
    // it (40001 would loop). Their messages are written for the user, e.g.
    // REG-954's "Submission #123 can't be deleted yet: ...".
    if (this.originalError?.code === 'PT409' && this.originalError?.message) {
      return this.originalError.message;
    }

    if (this.status === 404) {
      return 'Record not found';
    }

    if (this.status === 401) {
      return 'Authentication required';
    }

    if (this.status === 403) {
      return 'You do not have permission to perform this operation';
    }

    if (this.status === 409) {
      return 'This operation conflicts with existing data';
    }

    return this.message || 'An unexpected error occurred';
  }

  /**
   * Returns detailed error information for debugging
   */
  getDebugInfo(): DebugInfo {
    const debugInfo: DebugInfo = {
      operation: this.operation,
      tableName: this.tableName,
      relevantField: this.relevantField,
      status: this.status,
      isConstraintError: this.isConstraintError,
      constraintName: this.constraintInfo?.constraint_name,
      originalMessage: this.originalError?.message,
      dbCol: this.dbCol,
      code : this.originalError.code,
      details:this.originalError.details,
      hint:this.originalError.hint
    };

    // Add RPC-specific error fields if available
    if (this.originalError?.code) {
      debugInfo.code = this.originalError.code;
    }
    if (this.originalError?.details) {
      debugInfo.details = this.originalError.details;
    }
    if (this.originalError?.hint) {
      debugInfo.hint = this.originalError.hint;
    }

    return debugInfo;
  }
}

/**
 * Checks if an error contains a constraint name and matches it with constraint info
 * Since constraint names are unique across the database, no need to filter by table
 */
const getConstraintInfoFromError = async (error: any): Promise<ConstraintInfo | null> => {
  if (!error?.message) return null;
  
  try {
    const { data: constraintData } = await getConstraints();
    if (!constraintData) return null;
    
    // Check if error message contains any constraint names
    const errorMessage = error.message.toLowerCase();
    
    // Look for constraint info by checking if constraint name appears in error message
    for (const constraintName of Object.keys(constraintData.byConstraintName)) {
      if (errorMessage.includes(constraintName.toLowerCase())) {
        return constraintData.byConstraintName[constraintName];
      }
    }
    
    return null;
  } catch (e) {
    console.warn('Failed to fetch constraint info:', e);
    return null;
  }
};

/**
 * Enhanced error handler that enriches errors with constraint and field information
 */
const handleSupabaseError = async (
  error: any,
  operation: string,
  fieldName: string | null = null
): Promise<SupabaseOperationError> => {
  // Check for JWT expiration - trigger automatic logout
  if (error?.code === 'PGRST301' || error?.message?.includes('JWT expired')) {
    console.warn('JWT expired - signing out user');
    try {
      await signOutLocally();
    } catch (signOutError) {
      console.error('Error during automatic sign out:', signOutError);
    }
  }

  let status: number | null = null;
  let constraintInfo: ConstraintInfo | null = null;
  let dbCol: string | null = null;

  // Extract status from error if available
  if (error?.status) {
    status = error.status;
  } else if (error?.code) {
    // Map PostgreSQL error codes to HTTP-like status codes
    const statusMap: Record<string, number> = {
      '23505': 409, // unique_violation -> Conflict
      '23503': 409, // foreign_key_violation -> Conflict
      '23502': 400, // not_null_violation -> Bad Request
      '23514': 400, // check_violation -> Bad Request
      'PGRST116': 404, // No rows updated/deleted -> Not Found
      'PGRST301': 401, // JWT expired -> Unauthorized
    };
    status = statusMap[error.code as string] || 500;
  }
  
  // Try to get constraint information if this looks like a constraint error
  if (error?.message) {
    constraintInfo = await getConstraintInfoFromError(error);
  }
  
  // Get table name from constraint info if available
  const tableName = constraintInfo?.table_name;
  
  // Try to get field information if fieldName is provided and we have a table name
  if (fieldName && tableName) {
    try {
      const { data: fieldData } = await getFields();
      if (fieldData && fieldData.byTableName && fieldData.byTableName[tableName]) {
        const fieldInfo = fieldData.byTableName[tableName].byFieldName[fieldName];
        dbCol = fieldInfo ? fieldInfo.name : null;
      } else if (fieldData && fieldData[tableName]) {
        // Backward compatibility
        const fieldInfo = fieldData[tableName].byFieldName[fieldName];
        dbCol = fieldInfo ? fieldInfo.name : null;
      }
    } catch (e) {
      console.warn('Failed to fetch field info:', e);
    }
  }
  
  return new SupabaseOperationError({
    originalError: error,
    status: status || undefined,
    constraintInfo,
    relevantField: constraintInfo?.relevant_field || fieldName,
    dbCol,
    operation,
    tableName
  });
};

// Interface for cache invalidation options
interface CacheInvalidationOptions {
  invalidateCache?: boolean;
  cacheInvalidationType?: 'refresh' | 'noRefetch' | 'customQuery' | 'metadata';
  customQuery?: any;
}

/**
 * Helper function to handle cache invalidation based on options
 */
const handleCacheInvalidation = async (
  tableName: string, 
  options: CacheInvalidationOptions = {}
): Promise<void> => {
  const { 
    invalidateCache = false,
    cacheInvalidationType = 'refresh',
    customQuery = null
  } = options;

  if (!invalidateCache) return;

  switch (cacheInvalidationType) {
    case 'refresh':
      await invalidateAndRefreshAllQueriesForTable(tableName);
      break;
    case 'noRefetch':
      invalidateAllQueriesForTableNoRefetch(tableName);
      break;
    case 'metadata':
      await invalidateAllMetadataCache();
      break;
    case 'customQuery':
      if (customQuery) {
        invalidateAndRefetch(customQuery);
      } else {
        console.warn('customQuery required for customQuery invalidation type');
      }
      break;
    default:
      await invalidateAndRefreshAllQueriesForTable(tableName);
  }
};

// Interface for update operation options
interface UpdateRecordOptions extends CacheInvalidationOptions {
  returnData?: boolean;
  fieldName?: string | null;
  /**
   * Values the edit started from, for the columns being written. Turns the update into a
   * compare-and-set so a second writer can't silently overwrite the first — see
   * buildStaleWriteGuard.
   */
  expectedValues?: Record<string, any> | null;
  /** PostgreSQL data types for expectedValues, used to compare canonical wire values safely. */
  expectedValueTypes?: Record<string, string | null | undefined> | null;
}

/**
 * Field-scoped optimistic concurrency.
 *
 * The predicate goes on the columns actually being written, not on updated_at: background
 * writers (email ingestion, imports, migration jobs) bump updated_at constantly, and a
 * row-level version check would reject a rep's save because an unrelated column moved.
 * Guarding the written columns only rejects a genuine conflict — someone changed the same
 * field under us.
 *
 * Postgres evaluates this inside the UPDATE, so there is no window between the check and
 * the write. Columns whose loaded value isn't a scalar (jsonb blobs, arrays) are skipped:
 * PostgREST can't express equality for them reliably, and a wrong predicate would silently
 * match nothing, which reads as a permanent permission error.
 */
/**
 * REG-841: base tables whose rows carry a read-masked value (merchant Contact
 * Method). A base table can't mask per caller, so updates return only ids and
 * the guarded re-read goes through the masked generated view. Callers re-read
 * the record from its view after saving.
 */
const MASKED_BASE_TABLES: Record<string, string> = { 'public.merchant': 'merchant_view' };
const maskedReadSource = (schema: string, tableName: string): string | undefined =>
  MASKED_BASE_TABLES[`${schema}.${tableName}`];

const isGuardableValue = (value: any): boolean =>
  value === null
  || typeof value === 'string'
  || typeof value === 'number'
  || typeof value === 'boolean'
  || value instanceof Date;

interface StaleWriteGuard {
  column: string;
  value: any;
  filterValue: any;
  dataType?: string | null;
}

const buildStaleWriteGuard = (
  data: Partial<AnyRecord>,
  expectedValues: Record<string, any> | null | undefined,
  expectedValueTypes: Record<string, string | null | undefined> | null | undefined,
): StaleWriteGuard[] => {
  if (!expectedValues) return [];
  return Object.keys(data)
    .filter((column) => column in expectedValues && isGuardableValue(expectedValues[column]))
    .map((column) => {
      const value = expectedValues[column];
      const dataType = expectedValueTypes?.[column];
      return { column, value, filterValue: normalizeStaleWriteFilterValue(value, dataType), dataType };
    });
};

/**
 * Re-read the guarded columns after an update matched nothing.
 *
 * Three outcomes, and they need telling apart:
 *  - row not visible: the guard wasn't the reason (row gone, or RLS hides it)
 *  - guarded columns moved: a real conflict, someone wrote first
 *  - row visible and every guarded column still holds what we expected: the predicate
 *    itself failed to match, not the data — a client-side representation that doesn't
 *    round-trip (a Date vs the stored timestamptz text, a relationship's label vs its id).
 *    The caller retries once using the database's own wire values as a fresh atomic
 *    compare-and-set predicate.
 */
const inspectGuardedRow = async ({
  schema,
  tableName,
  whereClause,
  guards,
}: {
  schema: string;
  tableName: string;
  whereClause: Record<string, any>;
  guards: StaleWriteGuard[];
}): Promise<{ rowVisible: boolean; changedFields: string[]; currentValues: Record<string, any> }> => {
  try {
    let check = supabaseClient
      .schema(schema as any)
      .from(maskedReadSource(schema, tableName) ?? tableName)
      .select(guards.map((guard) => guard.column).join(','));

    Object.entries(whereClause).forEach(([key, value]) => {
      check = check.eq(key, value);
    });

    const { data: rows, error } = await check.limit(1);
    if (error || !Array.isArray(rows) || rows.length === 0) {
      return { rowVisible: false, changedFields: [], currentValues: {} };
    }

    const current = rows[0] as Record<string, any>;
    const changedFields = guards
      .filter((guard) => !databaseValuesAreEqual(current[guard.column], guard.value, guard.dataType))
      .map((guard) => guard.column);
    const currentValues = Object.fromEntries(guards.map((guard) => [guard.column, current[guard.column]]));
    return { rowVisible: true, changedFields, currentValues };
  } catch {
    // Diagnosis is best-effort; never let it mask the original failure.
    return { rowVisible: false, changedFields: [], currentValues: {} };
  }
};

const createStaleWriteError = ({
  schema,
  tableName,
  whereClause,
  fieldName,
  guards,
  changedFields,
  detectionStage,
}: {
  schema: string;
  tableName: string;
  whereClause: Record<string, any>;
  fieldName: string | null;
  guards: StaleWriteGuard[];
  changedFields: string[];
  detectionStage: StaleWriteDetectionStage;
}): SupabaseOperationError => {
  const error = new SupabaseOperationError({
    originalError: {
      message: `Someone else changed ${changedFields.join(', ')} while you were editing. Reload the record to see their change, then try again.`,
    },
    status: 409,
    operation: 'update',
    tableName,
    relevantField: fieldName ?? changedFields[0],
    staleFields: changedFields,
  });

  captureStaleWriteConflict({
    error,
    schema,
    tableName,
    whereClause,
    changedFields,
    guardedFields: guards.map(({ column }) => column),
    guardedTypes: Object.fromEntries(guards.map(({ column, dataType }) => [column, dataType ?? null])),
    detectionStage,
  });
  return error;
};

// Interface for update operation parameters
interface UpdateRecordParams {
  tableName: string;
  schema: string;
  data: Partial<AnyRecord>;
  whereClause: Record<string, any>;
  options?: UpdateRecordOptions;
}

// Interface for update operation result
interface UpdateRecordResult {
  data: AnyRecord[] | null;
  error: SupabaseOperationError | null;
}

/**
 * Enhanced update operation with error handling
 */
export const updateRecord = async ({
  tableName, 
  schema, 
  data, 
  whereClause, 
  options = {}
}: UpdateRecordParams): Promise<UpdateRecordResult> => {
  const {
    returnData = true,
    fieldName = null, // Primary field being updated (for better error context)
    expectedValues = null,
    expectedValueTypes = null,
  } = options;
  
  if (!schema) {
    throw new Error('A schema must be provided for the update operation.');
  }

  // Validate operation is allowed for this table category
  if (!isOperationAllowed('update', tableName, schema)) {
    const category = getTableCategory(tableName, schema);
    throw new Error(`Update operations are not allowed on ${category} tables like '${tableName}'`);
  }

  if (typeof whereClause !== 'object' || whereClause === null) {
    throw new Error('whereClause must be an object with column-value pairs');
  }

  // Compare-and-set on the columns being written
  const guards = buildStaleWriteGuard(data, expectedValues, expectedValueTypes);

  const runUpdate = async (applyGuards: boolean, filterValues?: Record<string, any>) => {
    let query = supabaseClient
      .schema(schema as any)
      .from(tableName)
      .update(data);

    Object.entries(whereClause).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    if (applyGuards) {
      guards.forEach(({ column, filterValue }) => {
        const predicateValue = filterValues && column in filterValues ? filterValues[column] : filterValue;
        query = predicateValue === null ? query.is(column, null) : query.eq(column, predicateValue);
      });
    }

    if (!returnData) return await query;
    return maskedReadSource(schema, tableName) ? await query.select('id') : await query.select();
  };

  try {
    let result = await runUpdate(guards.length > 0);

    if (result.error) {
      throw await handleSupabaseError(result.error, 'update', fieldName);
    }

    // RLS blocking a write is not a Postgrest error — the row is simply
    // excluded from the UPDATE, so Postgrest reports 200 with zero rows
    // matched. Without this check, an update a user isn't permitted to make
    // looks identical to a successful one (silent no-op).
    //
    // A failed stale-write guard lands here too and is indistinguishable at this point,
    // so re-read the row to tell the two apart: if it is still visible and the guarded
    // columns have moved, someone else got there first.
    if (returnData && Array.isArray(result.data) && result.data.length === 0) {
      const { rowVisible, changedFields, currentValues } = guards.length > 0
        ? await inspectGuardedRow({ schema, tableName, whereClause, guards })
        : { rowVisible: false, changedFields: [], currentValues: {} };

      if (changedFields.length > 0) {
        throw createStaleWriteError({
          schema,
          tableName,
          whereClause,
          fieldName,
          guards,
          changedFields,
          detectionStage: 'initial_guard',
        });
      }

      if (rowVisible) {
        // The data is untouched, but our original representation did not round-trip as a
        // filter. Retry atomically against the exact representation PostgREST just returned.
        // Do not retry unguarded: another writer could win between the re-read and retry.
        result = await runUpdate(true, currentValues);
        if (result.error) {
          throw await handleSupabaseError(result.error, 'update', fieldName);
        }

        if (returnData && Array.isArray(result.data) && result.data.length === 0) {
          const retryInspection = await inspectGuardedRow({ schema, tableName, whereClause, guards });
          if (retryInspection.changedFields.length > 0) {
            throw createStaleWriteError({
              schema,
              tableName,
              whereClause,
              fieldName,
              guards,
              changedFields: retryInspection.changedFields,
              detectionStage: 'retry_guard',
            });
          }
        }
      }
    }

    if (returnData && Array.isArray(result.data) && result.data.length === 0) {
      throw new SupabaseOperationError({
        originalError: { message: "This record couldn't be updated. It may no longer exist, or you may not have permission to edit it." },
        status: 403,
        operation: 'update',
        tableName,
        relevantField: fieldName,
      });
    }

    await handleCacheInvalidation(tableName, options);
    
    // Apply category-aware smart casting for proper typing
    const typedData = smartCast(result.data, schema, tableName);
    const typedRows = Array.isArray(typedData) ? typedData : typedData ? [typedData] : null;
    trackRecordOperation('updated', tableName, schema, typedRows, whereClause);
    
    return {
      data: typedRows,
      error: null
    };
    
  } catch (error) {
    trackRecordOperation('failed', tableName, schema, undefined, whereClause, 'update');
    if (error instanceof SupabaseOperationError) {
      return { data: null, error };
    }
    const supabaseError = await handleSupabaseError(error, 'update', fieldName);
    return { data: null, error: supabaseError };
  }
};

// Interface for insert operation options
interface InsertRecordOptions extends CacheInvalidationOptions {
  returnData?: boolean;
  onConflict?: string | null;
}

// Interface for insert operation parameters
interface InsertRecordParams {
  tableName: string;
  schema: string;
  data: Partial<AnyRecord> | Partial<AnyRecord>[];
  options?: InsertRecordOptions;
}

// Interface for insert operation result
interface InsertRecordResult {
  data: AnyRecord[] | null;
  error: SupabaseOperationError | null;
}

/**
 * Enhanced insert operation with error handling
 */
export const insertRecord = async ({
  tableName, 
  schema, 
  data, 
  options = {}
}: InsertRecordParams): Promise<InsertRecordResult> => {
  const { 
    returnData = true,
    onConflict = null // e.g., 'id' for upsert behavior
  } = options;

  if (!schema) {
    throw new Error('A schema must be provided for the insert operation.');
  }
  
  try {
    let query;
    
    if (onConflict) {
      // Use upsert when onConflict is specified
      query = supabaseClient
        .schema(schema as any)
        .from(tableName)
        .upsert(data, { onConflict: onConflict });
    } else {
      // Use regular insert when no onConflict
      query = supabaseClient
        .schema(schema as any)
        .from(tableName)
        .insert(data);
    }
    
    const finalQuery = returnData ? query.select() : query;
    
    const result = await finalQuery;
    
    if (result.error) {
      throw await handleSupabaseError(result.error, 'insert');
    }
    
    await handleCacheInvalidation(tableName, options);
    trackRecordOperation('created', tableName, schema, result.data || null);
    return {
      data: result.data || null,
      error: null
    };
    
  } catch (error) {
    trackRecordOperation('failed', tableName, schema, undefined, undefined, 'create');
    if (error instanceof SupabaseOperationError) {
      return { data: null, error };
    }
    const supabaseError = await handleSupabaseError(error, 'insert');
    return { data: null, error: supabaseError };
  }
};

/**
 * Enhanced upsert operation with error handling
 */
// Interface for upsert operation options
interface UpsertRecordOptions extends CacheInvalidationOptions {
  returnData?: boolean;
  onConflict?: string | null;
  ignoreDuplicates?: boolean;
}

// Interface for upsert operation parameters
interface UpsertRecordParams {
  tableName: string;
  schema: string;
  data: Partial<AnyRecord> | Partial<AnyRecord>[];
  options?: UpsertRecordOptions;
}

export const upsertRecord = async ({
  tableName, 
  schema, 
  data, 
  options = {}
}: UpsertRecordParams): Promise<InsertRecordResult> => {
  const { 
    returnData = true,
    onConflict = null, // No default conflict resolution column
    ignoreDuplicates = false
  } = options;
  
  if (!schema) {
    throw new Error('A schema must be provided for the upsert operation.');
  }

  try {
    const upsertOptions: { ignoreDuplicates: boolean; onConflict?: string } = { ignoreDuplicates };
    if (onConflict) {
      upsertOptions.onConflict = onConflict;
    }
    
    let query = supabaseClient
      .schema(schema as any)
      .from(tableName)
      .upsert(data, upsertOptions);
    
    const finalQuery = returnData ? query.select() : query;
    
    const result = await finalQuery;
    
    if (result.error) {
      throw await handleSupabaseError(result.error, 'upsert');
    }
    
    await handleCacheInvalidation(tableName, options);
    trackRecordOperation('upserted', tableName, schema, result.data || null);
    
    return {
      data: result.data || null,
      error: null
    };
    
  } catch (error) {
    trackRecordOperation('failed', tableName, schema, undefined, undefined, 'upsert');
    if (error instanceof SupabaseOperationError) {
      throw error;
    }
    throw await handleSupabaseError(error, 'upsert');
  }
};

/**
 * Enhanced delete operation with error handling
 */
// Interface for delete operation options
interface DeleteRecordOptions extends CacheInvalidationOptions {
  returnData?: boolean;
}

// Interface for delete operation parameters
interface DeleteRecordParams {
  tableName: string;
  schema: string;
  whereClause: Record<string, any>;
  options?: DeleteRecordOptions;
}

// Interface for delete operation result
interface DeleteRecordResult {
  data: AnyRecord[] | null;
  error: SupabaseOperationError | null;
}

export const deleteRecord = async ({
  tableName, 
  schema, 
  whereClause, 
  options = {}
}: DeleteRecordParams): Promise<DeleteRecordResult> => {
  const { 
    returnData = false 
  } = options;

  if (!schema) {
    throw new Error('A schema must be provided for the delete operation.');
  }
  
  try {
    let query = supabaseClient
      .schema(schema as any)
      .from(tableName)
      .delete();
    
    // Apply where clause
    if (typeof whereClause === 'object') {
      Object.entries(whereClause).forEach(([key, value]) => {
        query = query.eq(key, value);
      });
    } else {
      throw new Error('whereClause must be an object with column-value pairs');
    }
    
    const finalQuery = returnData ? query.select() : query;
    
    const result = await finalQuery;
    
    if (result.error) {
      throw await handleSupabaseError(result.error, 'delete');
    }
    
    // Check if any rows were actually deleted
    if (result.status === 204 && result.count === 0) {
      throw new SupabaseOperationError({
        originalError: null,
        status: 404,
        operation: 'delete',
        tableName
      });
    }
    
    await handleCacheInvalidation(tableName, options);
    
    // Apply category-aware smart casting for proper typing
    const typedData = smartCast(result.data, schema, tableName);
    const typedRows = Array.isArray(typedData) ? typedData : typedData ? [typedData] : null;
    trackRecordOperation('deleted', tableName, schema, typedRows, whereClause);
    
    return {
      data: typedRows,
      error: null
    };
    
  } catch (error) {
    trackRecordOperation('failed', tableName, schema, undefined, whereClause, 'delete');
    if (error instanceof SupabaseOperationError) {
      return { data: null, error };
    }
    const supabaseError = await handleSupabaseError(error, 'delete');
    return { data: null, error: supabaseError };
  }
};

/**
 * Enhanced RPC operation with error handling
 */
// Interface for RPC operation options
interface CallRpcOptions extends CacheInvalidationOptions {
  tableName?: string | null;
}

// Interface for RPC operation parameters
interface CallRpcParams {
  schema: string;
  functionName: string;
  params?: Record<string, any>;
  options?: CallRpcOptions;
}

// Interface for RPC operation result
interface CallRpcResult {
  data: any;
  count?: number | null;
  status?: number;
  statusText?: string;
}

export const callRpc = async ({
  schema, 
  functionName, 
  params = {}, 
  options = {}
}: CallRpcParams): Promise<CallRpcResult> => {
  const { 
    tableName = null // For cache invalidation
  } = options;

  if (!schema) {
    throw new Error('A schema must be provided for the RPC operation.');
  }

  if (!functionName) {
    throw new Error('A function name must be provided for the RPC operation.');
  }
  
  try {
    const result = await supabaseClient
      .schema(schema as any)
      .rpc(functionName, params);
    
    if (result.error) {
      throw await handleSupabaseError(result.error, 'rpc');
    }
    
    // Handle cache invalidation if tableName is provided
    if (tableName) {
      await handleCacheInvalidation(tableName, options);
    }
    
    return {
      data: result.data,
      count: result.count,
      status: result.status,
      statusText: result.statusText
    };
    
  } catch (error) {
    if (error instanceof SupabaseOperationError) {
      throw error;
    }
    throw await handleSupabaseError(error, 'rpc');
  }
};

/**
 * Utility function to handle errors in a consistent way across the application
 * This can be used in components to show appropriate user messages
 */
// Interface for operation error result
interface OperationErrorResult {
  userMessage: string;
  debugInfo: any;
  isConstraintError: boolean;
  status: number | null;
}

export const handleOperationError = (
  error: any, 
  defaultMessage: string = 'Operation failed'
): OperationErrorResult => {
  captureHandledOperationError(error);

  if (error instanceof SupabaseOperationError) {
    return {
      userMessage: error.getUserMessage(),
      debugInfo: error.getDebugInfo(),
      isConstraintError: error.isConstraintError,
      status: error.status ?? null
    };
  }
  
  return {
    userMessage: error?.message || defaultMessage,
    debugInfo: { originalError: error },
    isConstraintError: false,
    status: error?.status ?? null
  };
}; 
